import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import React from 'react';
import { ConfirmationDialog, useAsync } from 'react-components';
import { AppControllerContext } from '../app-contexts';

export interface CreateUserDialogProps {
  open: boolean;
  setOpen?: (open: boolean) => void;
  createUser?: (user: {
    username: string;
    isAdmin: boolean;
    email?: string;
    phoneNumber?: string;
    designation?: string;
    password: string;
  }) => Promise<void> | void;
}

export function CreateUserDialog({
  open,
  setOpen,
  createUser,
}: CreateUserDialogProps): JSX.Element {
  const safeAsync = useAsync();
  const [creating, setCreating] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [designation, setDesignation] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [usernameError, setUsernameError] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');
  const { showAlert } = React.useContext(AppControllerContext);

  const resetForm = () => {
    setUsername('');
    setIsAdmin(false);
    setEmail('');
    setPhoneNumber('');
    setDesignation('');
    setPassword('');
    setConfirmPassword('');
    setUsernameError(false);
    setPasswordError('');
  };

  const validateForm = () => {
    let valid = true;
    if (!username) {
      setUsernameError(true);
      valid = false;
    } else {
      setUsernameError(false);
    }
    if (!password) {
      setPasswordError('Password is required');
      valid = false;
    } else if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      valid = false;
    } else {
      setPasswordError('');
    }
    return valid;
  };

  const submitForm = async () => {
    if (!validateForm()) return;
    setCreating(true);
    try {
      createUser &&
        (await safeAsync(
          createUser({
            username,
            isAdmin,
            email: email || undefined,
            phoneNumber: phoneNumber || undefined,
            designation: designation || undefined,
            password,
          }),
        ));
      setCreating(false);
      resetForm();
      setOpen && setOpen(false);
    } catch (e) {
      setCreating(false);
      showAlert('error', `Failed to create user: ${(e as Error).message}`);
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      title="Create User"
      confirmText="Create"
      submitting={creating}
      onSubmit={submitForm}
      onClose={() => {
        resetForm();
        setOpen && setOpen(false);
      }}
    >
      <TextField
        id="username"
        variant="outlined"
        fullWidth
        autoFocus
        margin="normal"
        label="Username"
        value={username}
        onChange={(ev) => setUsername(ev.target.value)}
        error={usernameError}
        helperText={usernameError ? 'Required' : ''}
      />
      <TextField
        id="email"
        variant="outlined"
        fullWidth
        margin="normal"
        label="Email (optional)"
        type="email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
      />
      <TextField
        id="phone-number"
        variant="outlined"
        fullWidth
        margin="normal"
        label="Phone Number (optional)"
        value={phoneNumber}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        onChange={(ev) => setPhoneNumber(ev.target.value.replace(/\D/g, ''))}
      />
      <TextField
        id="designation"
        variant="outlined"
        fullWidth
        margin="normal"
        label="Designation (optional)"
        value={designation}
        onChange={(ev) => setDesignation(ev.target.value)}
      />
      <TextField
        id="password"
        variant="outlined"
        fullWidth
        margin="normal"
        label="Password"
        type="password"
        value={password}
        onChange={(ev) => setPassword(ev.target.value)}
        error={!!passwordError && !password}
        helperText={!password && passwordError ? passwordError : ''}
      />
      <TextField
        id="confirm-password"
        variant="outlined"
        fullWidth
        margin="normal"
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(ev) => setConfirmPassword(ev.target.value)}
        error={!!passwordError && !!password}
        helperText={password && passwordError ? passwordError : ''}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={isAdmin}
            onChange={(ev) => setIsAdmin(ev.target.checked)}
            id="is-admin-checkbox"
          />
        }
        label="Admin"
      />
    </ConfirmationDialog>
  );
}
